import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white text-gray-900 font-['Inter',sans-serif]">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-white">
          <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-blue-400/20 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-purple-400/20 to-transparent rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full text-white text-xs uppercase tracking-wider font-semibold mb-6 shadow-lg">
              About FluxByte
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              Building the <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Future</span><br />with Intelligent Systems
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              FluxByte is a leading AI solutions provider, transforming businesses through 
              cutting-edge machine learning, predictive analytics, and intelligent automation.
            </p>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-blue-600 to-purple-600 rounded-full">
                  <i className="ri-award-line text-white text-xl"></i>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">500+</div>
                  <div className="text-sm text-gray-600">AI Models</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-blue-600 to-purple-600 rounded-full">
                  <i className="ri-building-line text-white text-xl"></i>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">150+</div>
                  <div className="text-sm text-gray-600">Clients</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-gradient-to-bl from-purple-200/30 to-transparent rounded-full blur-3xl"></div>
        <div className="relative max-w-5xl mx-auto px-6">
          <div className="text-center">
            <div className="inline-block px-4 py-2 bg-blue-100 rounded-full text-blue-700 text-xs uppercase tracking-wider font-semibold mb-6">
              Our Mission
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 leading-tight">
              Empowering Organizations Through <br />
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Artificial Intelligence</span>
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
              We empower organizations to harness the full potential of artificial intelligence, 
              delivering scalable solutions that drive innovation, optimize operations, and create 
              lasting competitive advantages in an increasingly data-driven world.
            </p>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-gray-50 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-purple-100 rounded-full text-purple-700 text-xs uppercase tracking-wider font-semibold mb-6">
              Our Values
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Core <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Principles</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-lightbulb-flash-line',
                title: 'Innovation First',
                description: 'We push the boundaries of what\'s possible with AI, constantly exploring new technologies and methodologies to deliver cutting-edge solutions.',
                gradient: 'from-blue-500 to-cyan-500'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Trust & Transparency',
                description: 'We build AI systems with ethical considerations at the core, ensuring transparency, fairness, and accountability in every solution we deliver.',
                gradient: 'from-purple-500 to-pink-500'
              },
              {
                icon: 'ri-team-line',
                title: 'Client Success',
                description: 'Your success is our success. We partner closely with clients to understand their unique challenges and deliver AI solutions that drive real results.',
                gradient: 'from-indigo-500 to-blue-500'
              },
              {
                icon: 'ri-rocket-line',
                title: 'Excellence',
                description: 'We maintain the highest standards in AI development, from data quality to model performance, ensuring enterprise-grade solutions every time.',
                gradient: 'from-violet-500 to-purple-500'
              },
              {
                icon: 'ri-refresh-line',
                title: 'Continuous Learning',
                description: 'In the rapidly evolving field of AI, we stay ahead by continuously learning, adapting, and improving our capabilities and expertise.',
                gradient: 'from-blue-500 to-indigo-500'
              },
              {
                icon: 'ri-global-line',
                title: 'Scalable Impact',
                description: 'We design AI solutions that scale with your business, delivering sustainable value and adapting to your evolving needs over time.',
                gradient: 'from-purple-500 to-blue-500'
              }
            ].map((value, index) => (
              <div 
                key={index}
                className="group bg-white p-8 rounded-2xl hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-transparent hover:-translate-y-2 relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-purple-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative">
                  <div className={`w-16 h-16 flex items-center justify-center mb-6 bg-gradient-to-br ${value.gradient} rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-500`}>
                    <i className={`${value.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-purple-600 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">{value.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-blue-200/30 to-transparent rounded-full blur-3xl"></div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-blue-100 rounded-full text-blue-700 text-xs uppercase tracking-wider font-semibold mb-6">
              Our Team
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Expert <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Professionals</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Our multidisciplinary team brings together expertise in AI, machine learning, data science, and software engineering
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                icon: 'ri-brain-line',
                role: 'AI Researchers',
                description: 'PhDs and experts pushing the boundaries of machine learning and AI innovation',
                gradient: 'from-blue-500 to-cyan-500'
              },
              {
                icon: 'ri-code-s-slash-line',
                role: 'ML Engineers',
                description: 'Skilled engineers who build, train, and deploy production-ready AI models',
                gradient: 'from-purple-500 to-pink-500'
              },
              {
                icon: 'ri-bar-chart-box-line',
                role: 'Data Scientists',
                description: 'Analysts who extract insights and patterns from complex datasets',
                gradient: 'from-indigo-500 to-blue-500'
              },
              {
                icon: 'ri-settings-3-line',
                role: 'Solutions Architects',
                description: 'Experts who design scalable AI systems integrated with your infrastructure',
                gradient: 'from-violet-500 to-purple-500'
              }
            ].map((team, index) => (
              <div key={index} className="group bg-gray-50 p-8 rounded-2xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100 hover:border-transparent relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-purple-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative">
                  <div className={`w-16 h-16 flex items-center justify-center mb-6 bg-gradient-to-br ${team.gradient} rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-500`}>
                    <i className={`${team.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-purple-600 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">{team.role}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{team.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-purple-100 rounded-full text-purple-700 text-xs uppercase tracking-wider font-semibold mb-6">
              Our Impact
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Proven <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Results</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { number: '500+', label: 'AI Models Deployed', icon: 'ri-rocket-line', gradient: 'from-blue-500 to-cyan-500' },
              { number: '150+', label: 'Enterprise Clients', icon: 'ri-building-line', gradient: 'from-purple-500 to-pink-500' },
              { number: '98%', label: 'Client Satisfaction', icon: 'ri-star-line', gradient: 'from-indigo-500 to-blue-500' },
              { number: '24/7', label: 'Support Available', icon: 'ri-customer-service-line', gradient: 'from-violet-500 to-purple-500' }
            ].map((stat, index) => (
              <div key={index} className="group bg-white p-8 rounded-2xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100 hover:border-transparent text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-purple-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative">
                  <div className={`w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-gradient-to-br ${stat.gradient} rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-500`}>
                    <i className={`${stat.icon} text-3xl text-white`}></i>
                  </div>
                  <div className="text-5xl font-extrabold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-3">{stat.number}</div>
                  <div className="text-gray-600 text-sm font-medium">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-white">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-gradient-to-br from-blue-400/20 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-gradient-to-tl from-purple-400/20 to-transparent rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full text-white text-xs uppercase tracking-wider font-semibold mb-6 shadow-lg">
            Get Started
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Ready to Work <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">with Us?</span>
          </h2>
          <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
            Let's discuss how FluxByte can help transform your business with intelligent AI solutions
          </p>
          <Link 
            to="/contact"
            className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full font-semibold text-base text-white hover:shadow-2xl hover:shadow-purple-500/50 hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            Get in Touch
            <i className="ri-arrow-right-line text-xl"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
